/**
 * "Was that a tap, or the start of a drag?"
 *
 * Shared by every surface that dismisses when the player touches outside it:
 * GameModal's backdrop, and the floating table panels via
 * hooks/useOutsideTapDismiss.
 *
 * WHY MOVEMENT, AND NOT `pointercancel`
 * -------------------------------------
 * The obvious way to tell a scroll from a tap is to listen for `pointercancel`,
 * which fires when the browser claims a gesture for panning. That was the first
 * implementation here and it is the wrong primitive, for two reasons measured in
 * real engines (Chromium + WebKit, touch emulation, a scrollable scrim):
 *
 *  - When a scroll really does start, the engines send `pointercancel` and then
 *    NO `pointerup` at all — so there was never an event left to suppress. The
 *    handler was dead weight.
 *  - Its firing rules are engine- and device-specific, so relying on it makes
 *    dismissal behave differently across the web / iOS / Android builds, which
 *    all run this one client. A cancel that arrives for a stationary tap (which
 *    would be legal) silently kills tap-to-dismiss on that device.
 *
 * Distance travelled is the property we actually care about and it means the
 * same thing everywhere: a finger that stayed put wanted to tap, a finger that
 * moved wanted to scroll, drag a slider, or select text.
 */

/**
 * How far (px, per axis) a pointer may drift between down and up and still
 * count as a tap. Roughly the touch slop browsers themselves use before they
 * treat a gesture as a pan.
 */
export const TAP_SLOP_PX = 10;

export type TapOrigin = { x: number; y: number };

type PointerPosition = { clientX: number; clientY: number };

export const tapOrigin = ({ clientX, clientY }: PointerPosition): TapOrigin => ({
  x: clientX,
  y: clientY
});

/** True when the pointer ended close enough to `origin` to count as a tap. */
export const isWithinTapSlop = (origin: TapOrigin | null, event: PointerPosition): boolean =>
  !!origin &&
  Math.abs(event.clientX - origin.x) <= TAP_SLOP_PX &&
  Math.abs(event.clientY - origin.y) <= TAP_SLOP_PX;

/** How long to wait for the synthetic click before giving up on it. */
const CLICK_SWALLOW_MS = 400;

/**
 * Consume the `click` the browser synthesises for the tap we just used to
 * dismiss something, so that one gesture cannot also press whatever was
 * underneath.
 *
 * This is not hypothetical. A dismissing surface usually UNMOUNTS inside its
 * own `pointerup` handler, and on touch the engine hit-tests the follow-up
 * click afterwards — against the newly exposed DOM. Measured in both Chromium
 * and WebKit: a tap on GameModal's scrim positioned over the poker action bar
 * closed the fold-confirm AND fired FOLD, committing a real-money action the
 * player never chose. (With a mouse it is harmless: `click` retargets to the
 * common ancestor of press and release, which is still the scrim.)
 *
 * Scoped by POSITION rather than by a selector, because a full-viewport scrim
 * can be covering anything: only a click landing where the dismissing tap
 * happened belongs to that gesture. Anything else passes through untouched and
 * merely retires the listener — so does the timeout, for the touch paths that
 * never synthesise a click at all. Both matter: an armed listener left sitting
 * would turn this into an intermittent lost-action bug instead.
 */
export const swallowNextClickNear = (origin: TapOrigin): void => {
  let timeoutId = 0;

  const consume = (event: MouseEvent) => {
    if (isWithinTapSlop(origin, event)) {
      event.stopPropagation();
      event.preventDefault();
    }
    cleanup();
  };

  const cleanup = () => {
    window.clearTimeout(timeoutId);
    document.removeEventListener('click', consume, true);
  };

  document.addEventListener('click', consume, true);
  timeoutId = window.setTimeout(cleanup, CLICK_SWALLOW_MS);
};
